import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Flex, Text, Input, Button, FormControl, FormLabel, VStack, HStack, Image, Alert, AlertIcon, Link,
} from '@chakra-ui/react';
import { useAuth } from './AuthContext';

function Shell({ children }) {
  return (
    <Flex minH="100dvh" align="center" justify="center" px={5} bg="neutral.50">
      <Box w="100%" maxW="400px">
        <HStack spacing={2.5} mb={7} justify="center">
          <Image src="/laeebly-icon.png" alt="레이블리" w="28px" h="28px" />
          <Text fontSize="16px" fontWeight="600" letterSpacing="-0.02em">레이블리 도움말</Text>
        </HStack>
        <Box bg="white" borderWidth="1px" borderColor="neutral.200" borderRadius="8px" boxShadow="sm" p={7}>
          {children}
        </Box>
        <Text mt={5} fontSize="12.5px" color="neutral.500" textAlign="center" lineHeight="1.7">
          레이블리 계정으로 로그인합니다. 계정이 없다면 레이블리에서 먼저 가입해 주세요.
          <br />
          <Link as={RouterLink} to="/c-start" color="brand.700" textDecoration="underline"
            textUnderlineOffset="2px">가입 절차와 약관은 로그인 없이</Link> 보실 수 있습니다.
        </Text>
      </Box>
    </Flex>
  );
}

function PreviewPicker() {
  const { setPreviewRole } = useAuth();
  const roles = [
    { key: 'creator', label: '크리에이터로 보기', desc: '시작하기 + 크리에이터 문서' },
    { key: 'rights', label: '영상권리사로 보기', desc: '시작하기 + 권리사 문서' },
    { key: 'admin', label: '관리자로 보기', desc: '전체 문서' },
  ];
  return (
    <Shell>
      <Alert status="warning" borderRadius="6px" mb={5} fontSize="13px" alignItems="flex-start">
        <AlertIcon boxSize="15px" mt="2px" />
        미리보기 모드입니다. 실제 배포에서는 레이블리 계정으로 로그인하며, 역할은 계정에서 자동으로 판별됩니다.
      </Alert>
      <VStack spacing={2.5} align="stretch">
        {roles.map((r) => (
          <Button key={r.key} onClick={() => setPreviewRole(r.key)} variant="outline"
            borderColor="neutral.300" h="auto" py={3} justifyContent="flex-start"
            _hover={{ borderColor: 'brand.400', bg: 'brand.50' }}>
            <Box textAlign="left">
              <Text fontSize="14px" fontWeight="600" color="neutral.800">{r.label}</Text>
              <Text fontSize="12px" fontWeight="400" color="neutral.500">{r.desc}</Text>
            </Box>
          </Button>
        ))}
      </VStack>
    </Shell>
  );
}

export default function LoginPage() {
  const { signIn, isPreview, isConfigured } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (isPreview) return <PreviewPicker />;

  if (!isConfigured) {
    return (
      <Shell>
        <Alert status="error" borderRadius="6px" fontSize="13.5px" alignItems="flex-start">
          <AlertIcon boxSize="15px" mt="2px" />
          서버 설정이 없습니다. 배포 환경 변수 REACT_APP_SUPABASE_URL 과 REACT_APP_SUPABASE_ANON_KEY 를 확인해 주세요.
        </Alert>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try { await signIn(email.trim(), password); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <Text fontSize="15px" fontWeight="600" mb={1}>로그인</Text>
      <Text fontSize="13px" color="neutral.500" mb={5} lineHeight="1.6">
        가입 절차·약관을 뺀 나머지 문서는 <b>회원가입 승인 후 로그인</b>하시면 열립니다.
      </Text>
      <form onSubmit={submit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize="13px" color="neutral.600">이메일</FormLabel>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              autoComplete="email" bg="white" borderColor="neutral.300" fontSize="14px" />
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontSize="13px" color="neutral.600">비밀번호</FormLabel>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password" bg="white" borderColor="neutral.300" fontSize="14px" />
          </FormControl>
          {error && (
            <Alert status="error" borderRadius="6px" fontSize="13px" py={2.5}>
              <AlertIcon boxSize="14px" />{error}
            </Alert>
          )}
          <Button type="submit" isLoading={busy} bg="brand.500" color="white"
            _hover={{ bg: 'brand.600' }} fontSize="14px" h="42px">
            로그인
          </Button>
        </VStack>
      </form>
    </Shell>
  );
}
